import React, { Component, PropTypes } from 'react'
import Helmet from 'react-helmet'
import { Link as RouterLink } from 'react-router'
import { prefixLink } from 'gatsby-helpers'
import { config } from 'config'

import { colors, breakpoints } from '../constants/theme'
const { min1920, min1440, min1024, min768, min480, min360 } = breakpoints

import Section from '../components/Section'
import Column from '../components/Column'
import Row from '../components/Row'
import Spacer from '../components/Spacer'
import Container from '../components/Container'
import Page from '../components/Page'
import Icon from '../components/Icon'
import Image from '../components/Image'

import Heading1 from '../styleguide/Heading1'
import Heading2 from '../styleguide/Heading2'
import Heading3 from '../styleguide/Heading3'
import Heading4 from '../styleguide/Heading4'
import Heading5 from '../styleguide/Heading5'
import Heading6 from '../styleguide/Heading6'
import Paragraph from '../styleguide/Paragraph'
import Navigation from '../styleguide/Navigation'
import Link from '../styleguide/Link'
import LinkList from '../styleguide/LinkList'
import Footer from '../styleguide/Footer'
import Subtext from '../styleguide/Subtext'
import Services from '../styleguide/Services'
import Quote from '../styleguide/Quote'
import QuoteAuthor from '../styleguide/QuoteAuthor'
import Button from '../styleguide/Button'
import Line from '../styleguide/Line'

import FelaProvider from '../containers/FelaProvider'

import posts from '../constants/blogPosts'

const metaData = [
  {
    name: 'description',
    content: 'Ninja Concept Web Applications'
  },
  {
    name: 'keywords',
    content: 'web agency'
  }
]

const Navigator = () => (
  <Page
    id="navigation" style={{
      position: 'fixed',
      zIndex: 10,
      transformStyle: 'preserve-3d'
    }}
  >
    <Container color="white" flex="0 0 auto">
      <Helmet title="Ninja Concept | Sophisticated web &amp; mobile applications" meta={metaData} />
      <Row alignItems="center" justifyContent="flex-start" style={{ alignItems: 'flex-start' }}>
        <Column
          style={{
            flexDirection: 'row',
            justifyContent: 'flex-start'
          }} alignItems="center"
        >
          <img
            src={prefixLink('/images/logo/logo_ninjaconcept_horizontal.svg')}
            style={{ cursor: 'pointer' }}
            onClick={() => window.location.pathname = '/'}
          />
          <Spacer size={20} />
          <RouterLink to={prefixLink('/')}>
            <Navigation
              bold
              color="white"
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                alignSelf: 'flex-start'
              }}
            >
              <i
                className="fa fa-angle-left"
                aria-hidden="true"
                style={{
                  fontSize: 30,
                  marginTop: -4
                }}
              />
              <Spacer size={6} /> HOME
            </Navigation>
          </RouterLink>
        </Column>
      </Row>
    </Container>
  </Page>
)

const Post = ({ data, isLast }, { width, isRetina }) => (
  <Section flex="1 0 auto" width="100%" alignSelf="stretch">
    <Row
      order={5} orders={{ [min768]: 1 }} columns={{
        [min768]: 10,
        [min1440]: 8
      }}
    >
      <Column>
        <Section flex="1 0 auto" row style={{ alignSelf: 'flex-start' }}>
          <Section flex="0 0 auto" hideUntil={min768}>
            <Icon
              width={80}
              height={80}
              icon={prefixLink(`/images/avatar/avatar_${data.avatar}.png`)}
            />
          </Section>
          <Section flex="0 0 auto" hideAt={min768}>
            <Icon
              width={50}
              height={50}
              icon={prefixLink(`/images/avatar/avatar_${data.avatar}.png`)}
            />
          </Section>
          <Spacer size={20} />
          <Section flex="1 0 auto" alignSelf="center">
            <Paragraph flex style={{ textAlign: 'left' }}>
              <span
                style={{
                  color: colors.highlight,
                  paddingRight: 5
                }}
              >{data.author}</span>
              {
                data.topic ? <Section flex="1 0 auto" hideUntil={min768} row>
                  in<span
                    style={{
                      color: colors.highlight,
                      paddingLeft: 5
                    }}
                  >{data.topic}</span>
                </Section> : null
              }
            </Paragraph>
            <Section>
              <Paragraph style={{ textAlign: 'left' }}>{data.date} • {data.length}</Paragraph>
            </Section>
          </Section>
        </Section>
      </Column>
    </Row>
    {
      data.preview ? <Row
        order={1} orders={{ [min768]: 2 }} columns={{
          [min768]: 10,
          [min1440]: 8
        }}
      >
        <Column>
          <Spacer size={0} sizes={{ [min768]: 32 }} />
          <img
            src={`${prefixLink(data.preview) + (isRetina ? '@2px' : '')}.jpg`}
            width={width < 768 && width || '100%'}
            height="auto"
          />
        </Column>
      </Row> : null
    }
    <Row
      order={2} orders={{ [min768]: 3 }} columns={{
        [min768]: 10,
        [min1440]: 8
      }}
    >
      <Column>
        <Spacer size={20} sizes={{ [min768]: 30 }} />
        <Heading2
          style={{
            textAlign: 'left',
            alignSelf: 'flex-start'
          }}
        >{data.title}</Heading2>
      </Column>
    </Row>
    <Row
      order={3} orders={{ [min768]: 4 }} columns={{
        [min768]: 10,
        [min1440]: 8
      }}
    >
      <Column>
        <Spacer size={0} sizes={{ [min768]: 8 }} />
        <Section flex="1 0 auto" hideUntil={min768}>
          <Subtext style={{ textAlign: 'left' }}>{data.description}</Subtext>
        </Section>
      </Column>
    </Row>
    <Row
      order={4} orders={{ [min768]: 5 }} columns={{
        [min768]: 10,
        [min1440]: 8
      }}
    >
      <Column>
        <Spacer size={14} />
        <Section flex="1 0 auto" hideUntil={min768}>
          <a href={data.url}>
            <Paragraph style={{ textAlign: 'left' }} color={colors.highlight}>
              read more ...
            </Paragraph>
          </a>
        </Section>
      </Column>
    </Row>
    <Row
      order={6} columns={{
        [min768]: 10,
        [min1440]: 8
      }}
    >
      <Column
        sizes={{
          [min768]: 10,
          [min1440]: 8
        }}
      >
        <Spacer size={100} sizes={{ [min768]: 80 }} />
        {
          !isLast ? <Section flex="1 0 auto" alignSelf="stretch">
            <Line width="100%" stroke={2} color="rgba(40, 40, 40, 0.1)" />
            <Spacer size={0} sizes={{ [min768]: 80 }} />
          </Section> : null
        }
      </Column>
    </Row>
  </Section>
)

Post.contextTypes = {
  width: PropTypes.number,
  isRetina: PropTypes.bool
}

const Blog = (props, { width }) => (
  <FelaProvider>
    <Navigator />
    <Page
      data-scroll-scope="landing"
      bgCover
      bgImage={prefixLink('/images/images/blog_hero_bg')}
      id="headerImage"
      bgImages={{
        [min360]: min360,
        [min480]: min480,
        [min768]: min768,
        [min1024]: min1024,
        [min1440]: min1440
      }}
      height="100vh"
      heights={{ [min768]: '55vh' }}
    >
      <Container color="white" flex="1 0 auto">
        <Section flex="1 0 auto" alignSelf="stretch" center>
          <Helmet
            title="Ninja Concept | Sophisticated web &amp; mobile applications"
            meta={metaData}
          />
          <Row
            columns={{
              [min768]: 8,
              [min1920]: 6
            }}
          >
            <Column
              sizes={{
                [min768]: 8,
                [min1920]: 6
              }}
            >
              <Heading1 textAlign="center">
                Der NinjaConcept Blog
              </Heading1>
            </Column>
          </Row>
          <Spacer
            size={11} sizes={{
              [min480]: 14,
              [min768]: 18,
              [min1440]: 24
            }}
          />
          <Row
            columns={{
              [min1024]: 10,
              [min1920]: 8
            }}
          >
            <Column>
              <Subtext textAlign="center">
                Wir bloggen, weil wir fest an den Austausch von Erfahrungen und Erkenntnissen glauben. Finde heraus, über was wir zuletzt geschrieben haben und folge uns auf medium.com!
              </Subtext>
              <Spacer size="6%" />
            </Column>
          </Row>
        </Section>
      </Container>
    </Page>
    <Page bgColor="white" style={{ paddingTop: 0 }}>
      <Section flex="1 0 auto" width="100%" alignSelf="stretch">
        <Line width="100%" stroke={3} color="gray" />
      </Section>
      <Spacer
        size={0} sizes={{
          [min768]: 80,
          [min1440]: 120,
          [min1920]: 160
        }}
      />
      <Container color={colors.text} flex="1 0 auto">
        {posts.map((post, index) => <Post data={post} isLast={index === posts.length - 1} />)}
      </Container>
      <Spacer
        size={0} sizes={{
          [min768]: 80,
          [min1440]: 120,
          [min1920]: 160
        }}
      />
    </Page>
    <Page height="0%" data-scroll-scope="contact" bgColor={colors.background}>
      <Spacer
        size={120} sizes={{
          [min768]: 100,
          [min1024]: 120,
          [min1440]: 160
        }}
      />
      <Container color="white" flex="1 0 auto">
        <Row columns={{ [min768]: 12 }} center>
          <Column sizes={{ [min768]: 12 }}>
            <Section alignSelf="stretch" flex="1 0 auto" center>
              <Image svg base={prefixLink('/images/logo/logo_ninjaconcept_horizontal')} />
            </Section>
            <Spacer size={64} />
            <Footer bold textAlign="center">
              © 2016-2017 NinjaConcept GmbH
            </Footer>
            <Footer textAlign="center">
              The design of ninjaconcept is a registered trademark of NinjaConcept GmbH.
              <br />Hardtstraße 37A, 76185 Karlsruhe, Germany
            </Footer>
            <Section flex="1 0 auto" alignSelf="stretch" center row columnOnSmall>
              <Footer bold>
                <a href="tel:+49 721 180 352 30">+49 721 180 352 30</a>
              </Footer>
              <Spacer size={0} sizes={{ [min768]: 18 }} />
              <Footer bold>
                <a href="mailto:contact@ninjaconcept.com">contact@ninjaconcept.com</a>
              </Footer>
              <Spacer size={0} sizes={{ [min768]: 18 }} />
              <RouterLink to={prefixLink('/impressum/')}>
                <Footer bold>
                  Impressum & Datenschutzhinweis
                </Footer>
              </RouterLink>
            </Section>
          </Column>
        </Row>
      </Container>
      <Spacer
        size={60} sizes={{
          [min1440]: 68,
          [min1920]: 76
        }}
      />
    </Page>
  </FelaProvider>
)

Blog.contextTypes = { width: PropTypes.number }

class DeviceProvider extends Component {
  static childContextTypes = { width: PropTypes.number };

  constructor(props, context) {
    super(props, context)
    this.distance = 0
    this.state = { width: 0 }
  }

  updateDevice = () => {
    this.setState({ width: window.innerWidth })
  };

  draw = () => {
    window.requestAnimationFrame(this.draw)
    this.animate()
  };

  animate = () => {
    const scrollTop = this.mount.scrollTop

    const offset = scrollTop > this.mount.scrollHeight - window.innerHeight
      ? 0
      : this.lastScrollTop - scrollTop

    const newDirection = offset < 0 ? 'down' : offset > 0 ? 'up' : this.direction

    if (newDirection === this.direction && Date.now() >= window.IS_SCROLLING) {
      this.distance += Math.abs(offset)
    } else {
      this.distance = 0
    }

    if (Date.now() > window.LAST_SCROLL + 1000) {
      this.distance = 0
    }

    this.direction = newDirection

    this.lastScrollTop = scrollTop
    const scrollPercentage = scrollTop / (this.headerImage.offsetHeight - 40)

    this.nav.style.backgroundColor = `rgba(22, 24, 28, ${Math.max(
      0,
      Math.min(1.0, scrollPercentage)
    )})`

    if (this.direction === 'up' && this.distance > 200) {
      this.nav.style.transition = '200ms transform linear', this.nav.style.transform = 'translate3d(0, 0, 0)'
    }

    if (
      (this.direction === 'down' && this.distance > 70 || Date.now() > window.LAST_SCROLL + 3500) &&
      scrollTop > this.headerImage.offsetHeight - 40
    ) {
      this.nav.style.transition = '200ms transform linear', this.nav.style.transform = `translate3d(0, -${this.nav.offsetHeight}px, 0)`
    }

    window.LAST_SCROLL = Date.now()
  };

  getChildContext() {
    return { width: this.state.width }
  }

  componentDidMount() {
    this.mount = document.getElementById('react-mount')
    this.nav = document.getElementById('navigation')
    this.nav.classList.add('animated')
    this.headerImage = document.getElementById('headerImage')
    this.lastScrollTop = this.mount.scrollTop
    window.IS_SCROLLING = Date.now()

    this.draw()
    this.updateDevice()

    window.addEventListener('resize', () => {
      this.updateDevice()
    })
  }

  render() {
    return this.props.children
  }
}

export default () => (
  <DeviceProvider>
    <Blog />
  </DeviceProvider>
)



// WEBPACK FOOTER //
// ./blog.js